import { z } from "zod"

const sortedIds = z.array(z.string().min(1)).superRefine((values, ctx) => {
  if (values.some((value, index) => index > 0 && value <= values[index - 1]))
    ctx.addIssue({ code: "custom", message: "identifiers must be sorted and unique" })
})
const enforcement = z
  .object({
    hierarchyPruned: z.boolean(),
    resourceAudiences: z.boolean(),
    userAdministrationContained: z.boolean(),
    hoursScoped: z.boolean(),
    dashboardsScoped: z.boolean(),
  })
  .strict()
const capabilities = z
  .object({
    canViewCorporateRollup: z.boolean(),
    canManageCrossBoundaryUsers: z.boolean(),
    canCreateOrganizationAudienceResources: z.boolean(),
  })
  .strict()

export const OrganizationAccessContextV1Schema = z
  .object({
    contractVersion: z.literal(1),
    userId: z.string().min(1),
    organizationId: z.string().min(1),
    isolationMode: z.enum(["shared", "boundary-enforced"]),
    scopeRevision: z.string().startsWith("scope-v1-"),
    accessMode: z.enum(["full", "scoped", "none"]),
    assignmentRootEntityIds: sortedIds,
    visibleEntityIds: sortedIds.nullable(),
    memberDataBoundaryEntityIds: sortedIds,
    administrableEntityIds: sortedIds.nullable(),
    enforcement,
    capabilities,
  })
  .strict()
  .superRefine((value, ctx) => {
    const enforced = value.isolationMode === "boundary-enforced"
    const issue = (message: string) => ctx.addIssue({ code: "custom", message })
    if (Object.values(value.enforcement).some((flag) => flag !== enforced))
      issue("invalid enforcement")
    if (!enforced && value.memberDataBoundaryEntityIds.length) issue("invalid shared membership")
    if (value.accessMode === "full") {
      if (
        value.assignmentRootEntityIds.length !== 1 ||
        value.assignmentRootEntityIds[0] !== value.organizationId
      )
        issue("invalid full assignment")
      if (
        value.visibleEntityIds !== null ||
        value.administrableEntityIds !== null ||
        value.memberDataBoundaryEntityIds.length
      )
        issue("invalid full scope")
    } else if (value.accessMode === "scoped") {
      if (
        !value.assignmentRootEntityIds.length ||
        value.assignmentRootEntityIds.includes(value.organizationId) ||
        !value.visibleEntityIds?.length ||
        !value.administrableEntityIds?.length
      )
        issue("invalid scoped scope")
    } else if (
      value.assignmentRootEntityIds.length ||
      value.visibleEntityIds?.length ||
      value.visibleEntityIds === null ||
      value.memberDataBoundaryEntityIds.length ||
      value.administrableEntityIds?.length ||
      value.administrableEntityIds === null
    )
      issue("invalid none scope")
    const full = value.accessMode === "full"
    if (
      value.capabilities.canViewCorporateRollup !== full ||
      value.capabilities.canManageCrossBoundaryUsers !== full ||
      value.capabilities.canCreateOrganizationAudienceResources !==
        (value.accessMode !== "none" && (!enforced || full))
    )
      issue("invalid capabilities")
  })

const hierarchyCapabilities = z
  .object({
    readable: z.boolean(),
    selectable: z.boolean(),
    writable: z.boolean(),
    administrable: z.boolean(),
  })
  .strict()
export type OrganizationAccessHierarchyNodeV1 = {
  id: string
  parentId: string | null
  name: string
  type: string
  hierarchyLevel: number
  isActive?: boolean
  dataBoundaryEntityId: string | null
  isDataBoundaryRoot: boolean
  isContextOnly: boolean
  capabilities: z.infer<typeof hierarchyCapabilities>
  children: OrganizationAccessHierarchyNodeV1[]
}
const hierarchyNode: z.ZodType<OrganizationAccessHierarchyNodeV1> = z.lazy(() =>
  z
    .object({
      id: z.string().min(1),
      parentId: z.string().nullable(),
      name: z.string(),
      type: z.string(),
      hierarchyLevel: z.number(),
      isActive: z.boolean().optional(),
      dataBoundaryEntityId: z.string().nullable(),
      isDataBoundaryRoot: z.boolean(),
      isContextOnly: z.boolean(),
      capabilities: hierarchyCapabilities,
      children: z.array(hierarchyNode),
    })
    .strict()
    .superRefine((node, ctx) => {
      const { readable, selectable, writable, administrable } = node.capabilities
      const issue = (message: string) => ctx.addIssue({ code: "custom", message })
      if (selectable !== readable || writable !== readable) issue("invalid read capabilities")
      if (node.isContextOnly !== (!readable && !administrable)) issue("invalid context-only state")
      if (node.isDataBoundaryRoot !== (node.dataBoundaryEntityId === node.id))
        issue("invalid boundary-root state")
    }),
)
export const OrganizationAccessHierarchyV1Schema = z
  .object({
    contractVersion: z.literal(1),
    organizationId: z.string().min(1),
    isolationMode: z.enum(["shared", "boundary-enforced"]),
    scopeRevision: z.string().startsWith("scope-v1-"),
    hierarchy: hierarchyNode.nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.isolationMode !== "shared" || !value.hierarchy) return
    const stack = [value.hierarchy]
    while (stack.length) {
      const node = stack.pop()!
      if (node.dataBoundaryEntityId !== null || node.isDataBoundaryRoot) {
        ctx.addIssue({ code: "custom", message: "shared hierarchy cannot expose boundaries" })
        return
      }
      stack.push(...node.children)
    }
  })
export const ResourceAudienceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("system") }).strict(),
  z.object({ type: z.literal("organization") }).strict(),
  z
    .object({ type: z.literal("data-boundary"), dataBoundaryEntityId: z.string().trim().min(1) })
    .strict(),
])
const safeErrorBody = z
  .object({
    status: z.literal("error"),
    message: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    errors: z.array(z.unknown()).optional(),
  })
  .strict()
export const SafeCodedErrorResponseSchema = z.discriminatedUnion("statusCode", [
  z
    .object({
      statusCode: z.literal(403),
      body: safeErrorBody.extend({ code: z.enum(["FORBIDDEN", "PERMISSION_DENIED"]) }).strict(),
    })
    .strict(),
  z
    .object({
      statusCode: z.literal(404),
      body: safeErrorBody.extend({ code: z.literal("RESOURCE_NOT_FOUND") }).strict(),
    })
    .strict(),
  z
    .object({
      statusCode: z.literal(422),
      body: safeErrorBody.extend({ code: z.literal("INVALID_WRITE_TARGET") }).strict(),
    })
    .strict(),
  z
    .object({
      statusCode: z.literal(409),
      body: safeErrorBody.extend({ code: z.literal("SCOPE_REVISION_MISMATCH") }).strict(),
    })
    .strict(),
])
export type OrganizationAccessContextV1 = z.infer<typeof OrganizationAccessContextV1Schema>
export type OrganizationAccessHierarchyV1 = z.infer<typeof OrganizationAccessHierarchyV1Schema>
export type ResourceAudience = z.infer<typeof ResourceAudienceSchema>
